import React, { useRef, useEffect, type JSX } from 'react';
import useIntersectionObserver from '../../utils/useIntersectionObserver';
import { HydratedStoryPoint } from 'magical-trails';
import TrailPointItemIcon from './TrailPointItemIcon';
import { useScreenProperties } from 'magical-trails-react';
import { scrollIntoViewWithMotionPreference } from '../../utils/motionPreference';

export interface Props {
  point: HydratedStoryPoint;
  onSelect: () => void;
  openDirections: () => void;
  openTrailPointPage: () => void;
  selectedPoint: string;
  locationNumber?: number;
  showDirections?: boolean;
  // etc.
}

const TrailPointItem: React.FC<Props> = ({
  point,
  onSelect,
  openDirections,
  openTrailPointPage,
  selectedPoint,
  locationNumber,
  showDirections,
}) => {
  const liRef = useRef<HTMLLIElement>(null);
  const { cssBreakpoint } = useScreenProperties();

  // Intersection observer to see if we’re scrolled to the center
  const entry = useIntersectionObserver(liRef, {
    threshold: 0.75, // or 1.0 if you want it fully in view
  });

  // Ref-held (not state): a pending timer must be clearable synchronously —
  // including on unmount — without waiting on a re-render. The previous
  // useState-based handle had no cleanup path, so if this item unmounted
  // (e.g. the visitor scrolled away right as the intersection fired) while a
  // select-timer was pending, it fired after unmount and called onSelect()
  // on an already-gone item, orphaning the timer.
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const canSelect =
    point.type === 'location' &&
    selectedPoint !== point.code &&
    point.status !== 'hidden' &&
    !(point.config?.hideLocationPinOnComplete && point.status === 'completed');

  // Whenever the intersection changes, either start or clear a timer
  useEffect(() => {
    if (
      entry?.isIntersecting &&
      canSelect &&
      (cssBreakpoint === 'sm' || cssBreakpoint === 'md')
    ) {
      // Start a short timer so we don’t spam onSelect() if user swipes quickly
      hoverTimerRef.current = setTimeout(() => {
        onSelect();
        hoverTimerRef.current = null;
      }, 300);
    } else if (hoverTimerRef.current) {
      // Not intersecting => clear any existing timer
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }

    return () => {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }
    };
  }, [entry?.isIntersecting, selectedPoint]);

  // If this item is the “selectedPoint,” scroll it into view
  useEffect(() => {
    if (selectedPoint === point.code) {
      // small delay ensures the liRef is rendered
      setTimeout(() => {
        if (liRef.current) {
          scrollIntoViewWithMotionPreference(liRef.current, {
            block: 'nearest', // or 'center'
            inline: 'center', // important for horizontal snapping
          });
        }
      }, 0);
    }
  }, [selectedPoint, point.code]);

  const title =
    point.status === 'completed' || point.type !== 'location'
      ? point.title
      : point?.locationPoint?.title || `Location ${locationNumber}`;

  const handleSelect = () => {
    if (canSelect) onSelect();
  };

  return (
    <li
      className={`TrailPointItem ${
        selectedPoint === point.code ? 'TrailPointItem_selected' : ''
      } bg-mt-surface rounded-lg px-4 pt-5 pb-4 text-left shadow-lg`}
      ref={liRef}
    >
      <div className="flex h-full items-start">
        <TrailPointItemIcon type={point.type} status={point.status} />
        <div className="mt-0 ml-4 flex h-full grow flex-col justify-between text-left">
          {/*
            The row-selection action is a real, dedicated <button> — it
            carries the accessible name (the point's title) and handles
            Enter/Space/click natively. Directions/Open below are SIBLING
            controls, not descendants of it, so we never nest interactive
            elements inside an interactive element.
          */}
          <button
            type="button"
            className="focus-visible:outline-mt-primary w-full rounded-md text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-default"
            onClick={handleSelect}
            disabled={!canSelect}
          >
            <h3 className="text-mt-ink text-base leading-6 font-semibold">
              {title}
            </h3>
          </button>
          {/* The author took this stop off the trail after the player had
              already finished it, so it stays on their list (done is done)
              with a note, and loses the controls that no longer apply. */}
          {point.removedFromTrail && (
            <p className="text-mt-ink-muted mt-1 text-sm">
              No longer part of the trail
            </p>
          )}
          <div className="mt-4 flex justify-end">
            {point.type === 'location' &&
              showDirections &&
              !point.removedFromTrail && (
                <button
                  type="button"
                  className="bg-mt-surface text-mt-ink ring-mt-border hover:bg-mt-surface-2 mr-4 rounded-md px-3 py-2 text-sm font-semibold shadow-xs ring-1 ring-inset"
                  onClick={openDirections}
                >
                  Directions
                </button>
              )}
            <button
              type="button"
              className="mt-btn bg-mt-primary text-mt-on-primary hover:bg-mt-primary-hover rounded-md px-3 py-2 text-sm font-semibold shadow-xs"
              onClick={() => {
                point.status !== 'locked' && openTrailPointPage();
              }}
              disabled={point.status === 'locked'}
            >
              Open
            </button>
          </div>
        </div>
      </div>
    </li>
  );
};

export default TrailPointItem;
